import React, { useEffect, useState, useRef } from 'react';
import { toolboxService } from '../../../services/api';

const Step2_Scraping = ({ urls, onComplete, onBack }) => {
  const [progress, setProgress] = useState({});
  const [results, setResults] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const isMountedRef = useRef(true);
  const abortRef = useRef(null);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  useEffect(() => {
    if (urls.length > 0 && !isProcessing) {
      runScraping();
    }
  }, []);

  const runScraping = async () => {
    setIsProcessing(true);
    abortRef.current = new AbortController();
    const signal = abortRef.current.signal;

    urls.forEach((url, i) => {
      setProgress((p) => ({ ...p, [url]: { status: 'processing', index: i + 1, total: urls.length } }));
    });

    const out = [];
    for (let i = 0; i < urls.length; i++) {
      const url = urls[i];
      if (!isMountedRef.current || signal?.aborted) break;
      try {
        const data = await toolboxService.flexibleExtractScrapeOnly(url, signal);
        if (!isMountedRef.current || signal?.aborted) break;
        if (data.success && data.content) {
          setProgress((p) => ({ ...p, [url]: { status: 'success', index: i + 1, total: urls.length } }));
          out.push({ url, success: true, content: data.content });
        } else {
          setProgress((p) => ({ ...p, [url]: { status: 'error', index: i + 1, total: urls.length, error: data.error || 'No content' } }));
          out.push({ url, success: false, error: data.error || 'No content' });
        }
      } catch (err) {
        if (!isMountedRef.current || signal?.aborted) break;
        const msg = err.response?.data?.detail || err.message || 'Request failed';
        setProgress((p) => ({ ...p, [url]: { status: 'error', index: i + 1, total: urls.length, error: msg } }));
        out.push({ url, success: false, error: msg });
      }
    }

    if (isMountedRef.current && !signal?.aborted) {
      setResults(out);
      setIsProcessing(false);
    }
  };

  const getIcon = (status) => {
    if (status === 'processing') {
      return (
        <svg className="animate-spin h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      );
    }
    if (status === 'success') {
      return (
        <svg className="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      );
    }
    return (
      <svg className="h-5 w-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
    );
  };

  const [expandedUrl, setExpandedUrl] = useState(null);
  const completed = Object.values(progress).filter((p) => p.status === 'success' || p.status === 'error').length;
  const successCount = Object.values(progress).filter((p) => p.status === 'success').length;
  const total = urls.length;
  const done = completed === total && total > 0;

  const getResultForUrl = (url) => results.find((r) => r.url === url);

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Step 2: Scraping</h2>
        <p className="text-gray-600">Fetching page content from each URL (temp saved for next step)...</p>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex justify-between items-center mb-2">
          <span className="font-medium text-blue-900">Progress</span>
          <span className="text-blue-700">{completed} / {total} completed</span>
        </div>
        <div className="w-full bg-blue-200 rounded-full h-2">
          <div
            className="bg-blue-600 h-2 rounded-full transition-all"
            style={{ width: total ? `${(completed / total) * 100}%` : 0 }}
          />
        </div>
        <p className="text-sm text-blue-700 mt-1">{successCount} successful, {completed - successCount} failed</p>
      </div>

      <div className="space-y-2">
        {urls.map((url, i) => {
          const p = progress[url] || { status: 'pending', index: i + 1, total, error: null };
          const result = getResultForUrl(url);
          const hasContent = result?.success && result?.content;
          const isExpanded = expandedUrl === url;
          return (
            <div
              key={i}
              className={`border rounded-md overflow-hidden ${
                p.status === 'success' ? 'bg-green-50 border-green-200' :
                p.status === 'error' ? 'bg-red-50 border-red-200' :
                p.status === 'processing' ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-200'
              }`}
            >
              <div className="flex items-center gap-3 p-3">
                {getIcon(p.status)}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">[{p.index}/{p.total}] {url}</div>
                  {p.error && <div className="text-xs text-red-600 mt-0.5">{p.error}</div>}
                </div>
                {hasContent && (
                  <button
                    type="button"
                    onClick={() => setExpandedUrl(isExpanded ? null : url)}
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium whitespace-nowrap"
                  >
                    {isExpanded ? 'Hide preview' : 'Preview'}
                  </button>
                )}
              </div>
              {hasContent && isExpanded && (
                <div className="border-t border-gray-200 bg-white p-3">
                  <div className="text-xs text-gray-500 mb-1">
                    Content ({result.content.length} chars)
                  </div>
                  <pre className="text-xs text-gray-800 whitespace-pre-wrap break-words max-h-64 overflow-y-auto bg-gray-50 p-3 rounded border border-gray-200 font-sans">
                    {result.content}
                  </pre>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {done && (
        <div className="flex justify-between">
          <button
            type="button"
            onClick={onBack}
            className="px-6 py-3 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 font-medium"
          >
            Back
          </button>
          <button
            type="button"
            onClick={() => onComplete(results)}
            disabled={successCount === 0}
            className="px-8 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 font-medium"
          >
            {successCount === 0 ? 'No content' : `Continue to Analyze (${successCount} pages)`}
          </button>
        </div>
      )}
    </div>
  );
};

export default Step2_Scraping;
